import { ArrowLeft, Loader2, Lock, Send, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { AlpanaDivider } from "@/components/tss/Ornaments";
import { ChangeNotice } from "@/components/tss/VoteChange";

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const isCanadianPhone = (v) => {
  const s = v.trim();
  if (!/^[+\d\s\-().]+$/.test(s) || (s.startsWith("+") && !s.startsWith("+1"))) return false;
  let d = s.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  return d.length === 10 && !"01".includes(d[0]);
};

const input = "mt-1 h-12 w-full rounded-xl border bg-white px-4 text-base outline-none focus:ring-2 focus:ring-gold/30";

const Field = ({ label, error, ...props }) => (
  <label className="block text-sm font-semibold text-ink">
    {label}
    <input {...props} className={`${input} ${error ? "border-red-400" : "border-gold/50 focus:border-gold"}`} />
    {error && <span className="mt-1 block text-xs font-medium text-red-600" data-testid={`${props["data-testid"]}-error`}>{error}</span>}
  </label>
);

export const ReviewSheet = ({ picks, cats, coms, details, onChange, onBack, onSubmit, submitting, ballot }) => {
  const set = (k) => (e) => onChange({ ...details, [k]: e.target.value });
  const emailErr = details.email.trim() && !EMAIL_RE.test(details.email.trim()) ? "Please enter a valid email" : "";
  const phoneErr = details.phone.trim() && !isCanadianPhone(details.phone) ? "Please enter a 10-digit Canadian number, e.g. (416) 555-1234" : "";
  const ready = details.name.trim() && details.email.trim() && details.phone.trim() && !emailErr && !phoneErr;

  return (
    <section data-testid="review-screen" className="rise mx-auto max-w-2xl px-4 pb-10 pt-6">
      <button type="button" onClick={onBack} data-testid="review-back-btn" className="inline-flex items-center gap-1 text-sm font-medium text-maroon">
        <ArrowLeft className="h-4 w-4" /> Change picks
      </button>
      <h2 className="mt-3 font-display text-3xl font-bold text-maroon">{ballot.count ? "Review your change" : "Review your votes"}</h2>
      <p className="font-bengali text-[15px] text-ink/80">আপনার ভোট যাচাই করুন</p>

      <ul className="mt-5 space-y-2" data-testid="review-list">
        {picks.map(([catId, comId]) => {
          const cat = cats.find((c) => c.id === catId);
          return (
            <li key={catId} className="rounded-xl border border-gold/40 bg-white p-3" data-testid={`review-item-${cat?.key || catId}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{cat?.name_en} · <span className="font-bengali normal-case tracking-normal">{cat?.name_bn}</span></p>
              <p className="mt-0.5 font-display text-lg font-bold text-maroon">{coms.find((c) => c.id === comId)?.name}</p>
            </li>
          );
        })}
      </ul>

      <div className="mt-4"><ChangeNotice ballot={ballot} /></div>
      <AlpanaDivider className="my-6" />

      <div className="rounded-2xl border border-gold bg-white p-5 shadow-festive" data-testid="voter-details-form">
        <h3 className="font-display text-xl font-bold text-maroon">Your details</h3>
        <div className="mt-3 flex gap-3 rounded-xl bg-forest/10 p-3" data-testid="voter-details-disclaimer">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-forest" />
          <div className="text-[13px] leading-relaxed text-ink">
            <p>Your vote is anonymous — your choices will never be linked to you publicly. We collect your name, email and phone for verification only.</p>
            <p className="mt-2 font-bengali">আপনার ভোট সম্পূর্ণ গোপন — আপনার পছন্দ কারো সাথে প্রকাশ করা হবে না। শুধুমাত্র যাচাইয়ের জন্য আপনার নাম, ইমেইল ও ফোন নেওয়া হচ্ছে।</p>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <Field label="Name *" value={details.name} onChange={set("name")} autoComplete="name" maxLength={100} data-testid="voter-name-input" />
          <Field label="Email *" type="email" inputMode="email" value={details.email} onChange={set("email")} autoComplete="email" maxLength={200} error={emailErr} data-testid="voter-email-input" />
          <Field label="Phone *" type="tel" inputMode="tel" placeholder="(416) 555-1234" value={details.phone} onChange={set("phone")} autoComplete="tel" maxLength={25} error={phoneErr} data-testid="voter-phone-input" />
        </div>
        <div data-testid="privacy-statement" className="mt-4 flex gap-2.5 rounded-xl border border-gold/40 bg-ivory p-3 text-[13px] leading-relaxed text-ink">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-maroon" />
          <p>
            <b>Your privacy matters to us.</b> Your name, email, and phone number are collected only to keep voting fair (one vote per person) and to send you Sharod Shamman updates if you agree below. We never sell or share your personal information with anyone. Your data is stored securely, kept confidential, and accessible only to the TSS organizing team.{" "}
            <Link to="/privacy" target="_blank" data-testid="privacy-statement-link" className="font-semibold text-maroon underline">Read our Privacy Policy</Link>
          </p>
        </div>
        <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl p-1 text-[14px] leading-snug text-ink">
          <Checkbox checked={details.consent} onCheckedChange={(v) => onChange({ ...details, consent: v === true })} data-testid="marketing-consent-checkbox" className="mt-0.5 h-5 w-5 border-maroon data-[state=checked]:bg-maroon" />
          <span>Yes, TSS may send me updates and announcements about Sharod Shamman and future community events by email or SMS.</span>
        </label>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!ready || submitting}
          data-testid="submit-votes-btn"
          className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-forest font-semibold text-white shadow-festive transition-[transform,background-color,opacity] duration-200 hover:bg-forest-light active:scale-95 disabled:opacity-40"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit Votes
        </button>
      </div>
    </section>
  );
};
